import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import * as storage from '@/lib/workspace/storage';
import {
  exportEncryptedWorkspaceToFile,
  exportWorkspaceToFile,
  importWorkspaceFromJson,
  readImportFile,
} from '@/lib/workspace/export';
import { useVault } from '@/hooks/useVault';
import {
  DEFAULT_PROJECT_INSTRUCTIONS,
  DEFAULT_WORKSPACE_SETTINGS,
  isFreestandingChat,
  type Chat,
  type ContextItem,
  type HistoryMode,
  type MemoryItem,
  type Message,
  type Project,
  type WorkspaceSettings,
} from '@/lib/workspace/types';
import { titleFromMessage } from '@/lib/workspace/prompt';
import { attachmentSummary } from '@/lib/workspace/attachments';

export interface WorkspaceSearchHit {
  /** Chat to open (optional for project-only hits). */
  chatId?: string;
  chatTitle: string;
  projectId?: string | null;
  projectName?: string;
  messageId?: string;
  snippet: string;
  role?: Message['role'];
  updatedAt: number;
  /** Where the match came from (for UI badges). */
  matchKind?: 'title' | 'message' | 'attachment' | 'memory' | 'context' | 'project';
}

interface WorkspaceContextValue {
  ready: boolean;
  settings: WorkspaceSettings;
  projects: Project[];
  chats: Chat[];
  /** Chats not attached to any project (Recents). */
  recentChats: Chat[];
  messages: Message[];
  activeProject: Project | null;
  activeChat: Chat | null;

  setHistoryMode: (mode: HistoryMode) => Promise<void>;
  setDefaultModelId: (modelId: string | undefined) => Promise<void>;
  /** Global system instructions for every chat (Settings → Instructions). */
  setGlobalInstructions: (text: string) => Promise<void>;
  /** Patch arbitrary workspace settings (idle lock, sync options, …). */
  updateSettings: (patch: Partial<WorkspaceSettings>) => Promise<void>;

  selectProject: (projectId: string) => Promise<void>;
  selectChat: (chatId: string) => Promise<void>;

  createProject: (name?: string) => Promise<Project>;
  updateProject: (id: string, patch: Partial<Project>) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;

  /**
   * Create a chat. Pass a project id to attach it, or omit / pass null for a
   * freestanding chat (shown under Recents).
   */
  createChat: (projectId?: string | null) => Promise<Chat>;
  /** Move a chat into a project, or out to freestanding (null). */
  moveChatToProject: (chatId: string, projectId: string | null) => Promise<void>;
  updateChat: (id: string, patch: Partial<Chat>) => Promise<void>;
  deleteChat: (id: string) => Promise<void>;
  clearChatMessages: (chatId: string) => Promise<void>;

  addMessage: (message: Omit<Message, 'id' | 'createdAt'> & { id?: string; createdAt?: number }) => Promise<Message>;
  updateMessage: (id: string, patch: Partial<Message>) => Promise<void>;
  deleteMessage: (id: string) => Promise<void>;
  replaceMessages: (chatId: string, messages: Message[]) => Promise<void>;
  /**
   * Fork a new chat from an existing one, copying messages up to and including
   * `throughMessageId` (or all messages if omitted).
   */
  forkChat: (sourceChatId: string, throughMessageId?: string) => Promise<Chat>;
  /** Search chat titles + message bodies (vault must be unlocked for sealed data). */
  searchWorkspace: (query: string, limit?: number) => Promise<WorkspaceSearchHit[]>;

  addContextItem: (projectId: string, item: Omit<ContextItem, 'id' | 'createdAt'>) => Promise<void>;
  updateContextItem: (projectId: string, itemId: string, patch: Partial<ContextItem>) => Promise<void>;
  removeContextItem: (projectId: string, itemId: string) => Promise<void>;

  addMemoryItem: (
    projectId: string,
    item: Omit<MemoryItem, 'id' | 'createdAt' | 'updatedAt'>,
  ) => Promise<void>;
  updateMemoryItem: (
    projectId: string,
    itemId: string,
    patch: Partial<MemoryItem>,
  ) => Promise<void>;
  removeMemoryItem: (projectId: string, itemId: string) => Promise<void>;

  exportAll: () => Promise<void>;
  exportProject: (projectId: string) => Promise<void>;
  exportEncrypted: (passphrase: string) => Promise<void>;
  importFile: (
    file: File,
    mode?: 'merge' | 'replace',
    passphrase?: string,
  ) => Promise<void>;

  refresh: () => Promise<void>;
  shouldPersist: boolean;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

/**
 * Bootstrap workspace. Prefer last active chat (including freestanding).
 * Does not force a default project — users can live entirely in Recents.
 */
async function ensureWorkspace(): Promise<{
  settings: WorkspaceSettings;
  projects: Project[];
  project: Project | null;
  chat: Chat;
}> {
  let settings = await storage.getSettings();
  const projects = await storage.listProjects();
  const allChats = await storage.getAllChats();

  // 1) Restore last active chat if still present
  if (settings.activeChatId) {
    const existing =
      allChats.find((c) => c.id === settings.activeChatId) ??
      (await storage.getChat(settings.activeChatId));
    if (existing) {
      const project = existing.projectId
        ? projects.find((p) => p.id === existing.projectId) ?? null
        : null;
      return { settings, projects, project, chat: existing };
    }
  }

  // 2) Prefer most recently updated chat of any kind
  const sorted = [...allChats].sort((a, b) => b.updatedAt - a.updatedAt);
  if (sorted[0]) {
    const chat = sorted[0];
    const project = chat.projectId
      ? projects.find((p) => p.id === chat.projectId) ?? null
      : null;
    settings = {
      ...settings,
      activeChatId: chat.id,
      activeProjectId: project?.id,
      ...(project
        ? {
            activeChatByProject: {
              ...settings.activeChatByProject,
              [project.id]: chat.id,
            },
          }
        : {}),
    };
    await storage.saveSettings(settings);
    return { settings, projects, project, chat };
  }

  // 3) Empty workspace → create one freestanding chat (Claude-style default)
  const now = Date.now();
  const chat: Chat = {
    id: crypto.randomUUID(),
    projectId: null,
    title: 'New chat',
    createdAt: now,
    updatedAt: now,
  };
  await storage.putChat(chat);
  settings = {
    ...settings,
    activeChatId: chat.id,
    activeProjectId: undefined,
  };
  await storage.saveSettings(settings);
  return { settings, projects, project: null, chat };
}

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { status: vaultStatus, unlocked } = useVault();
  const [ready, setReady] = useState(false);
  const [settings, setSettings] = useState<WorkspaceSettings>(DEFAULT_WORKSPACE_SETTINGS);
  const [projects, setProjects] = useState<Project[]>([]);
  /** All chats across projects (sidebar); messages only for active chat */
  const [chats, setChats] = useState<Chat[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [activeProjectId, setActiveProjectId] = useState<string | undefined>();
  const [activeChatId, setActiveChatId] = useState<string | undefined>();
  /** Always-current settings for async writers (avoids stale closure clobber). */
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  const shouldPersist = settings.historyMode !== 'ephemeral';

  const loadMessages = useCallback(async (chatId: string) => {
    const msgs = await storage.listMessages(chatId);
    setMessages(msgs);
  }, []);

  const reloadAllChats = useCallback(async () => {
    const all = await storage.getAllChats();
    setChats(all.sort((a, b) => b.updatedAt - a.updatedAt));
  }, []);

  const refresh = useCallback(async () => {
    const { settings: s, projects: p, project, chat } = await ensureWorkspace();
    setSettings(s);
    setProjects(p);
    setActiveProjectId(project?.id);
    setActiveChatId(chat.id);
    await reloadAllChats();
    await loadMessages(chat.id);
    setReady(true);
  }, [loadMessages, reloadAllChats]);

  // Load workspace when vault is ready (unlocked, no vault, or no user)
  useEffect(() => {
    if (
      vaultStatus === 'loading' ||
      vaultStatus === 'unlocking' ||
      vaultStatus === 'locked'
    ) {
      setReady(false);
      return;
    }
    refresh().catch(console.error);
  }, [refresh, vaultStatus, unlocked]);

  const persistSettings = useCallback(async (next: WorkspaceSettings) => {
    settingsRef.current = next;
    setSettings(next);
    await storage.saveSettings(next);
  }, []);

  const setHistoryMode = useCallback(
    async (mode: HistoryMode) => {
      await persistSettings({ ...settingsRef.current, historyMode: mode });
    },
    [persistSettings],
  );

  const setDefaultModelId = useCallback(
    async (modelId: string | undefined) => {
      await persistSettings({ ...settingsRef.current, defaultModelId: modelId });
    },
    [persistSettings],
  );

  const setGlobalInstructions = useCallback(
    async (text: string) => {
      await persistSettings({ ...settingsRef.current, globalInstructions: text });
    },
    [persistSettings],
  );

  /**
   * Patch settings from IDB + React, then update both.
   * Used by sync meta (publish/pull times) so sidebar + Privacy stay fresh.
   */
  const updateSettings = useCallback(
    async (patch: Partial<WorkspaceSettings>) => {
      const fromDisk = await storage.getSettings();
      const next = { ...fromDisk, ...settingsRef.current, ...patch };
      await persistSettings(next);
    },
    [persistSettings],
  );

  const selectProject = useCallback(
    async (projectId: string) => {
      const project = projects.find((p) => p.id === projectId) ?? (await storage.getProject(projectId));
      if (!project) return;

      let projectChats = await storage.listChats(projectId);
      let chatId = settings.activeChatByProject[projectId];
      let chat = projectChats.find((c) => c.id === chatId);

      if (!chat) {
        if (projectChats.length === 0) {
          const now = Date.now();
          chat = {
            id: crypto.randomUUID(),
            projectId,
            title: 'New chat',
            createdAt: now,
            updatedAt: now,
          };
          if (shouldPersist) await storage.putChat(chat);
          projectChats = [chat];
        } else {
          chat = projectChats[0];
        }
        chatId = chat.id;
      }

      const nextSettings: WorkspaceSettings = {
        ...settings,
        activeChatId: chat.id,
        activeProjectId: projectId,
        activeChatByProject: {
          ...settings.activeChatByProject,
          [projectId]: chat.id,
        },
      };
      await persistSettings(nextSettings);
      setActiveProjectId(projectId);
      setActiveChatId(chat.id);
      await reloadAllChats();
      await loadMessages(chat.id);
    },
    [projects, settings, shouldPersist, persistSettings, loadMessages, reloadAllChats],
  );

  const selectChat = useCallback(
    async (chatId: string) => {
      const chat =
        chats.find((c) => c.id === chatId) ?? (await storage.getChat(chatId));
      if (!chat) return;

      const freestanding = isFreestandingChat(chat);
      const nextSettings: WorkspaceSettings = {
        ...settings,
        activeChatId: chatId,
        activeProjectId: freestanding ? undefined : (chat.projectId ?? undefined),
        activeChatByProject: freestanding
          ? settings.activeChatByProject
          : {
              ...settings.activeChatByProject,
              [chat.projectId!]: chatId,
            },
      };
      await persistSettings(nextSettings);
      setActiveProjectId(freestanding ? undefined : (chat.projectId ?? undefined));
      setActiveChatId(chatId);
      await loadMessages(chatId);
    },
    [chats, settings, persistSettings, loadMessages],
  );

  const createProject = useCallback(
    async (name = 'New project') => {
      const now = Date.now();
      const project: Project = {
        id: crypto.randomUUID(),
        name,
        instructions: DEFAULT_PROJECT_INSTRUCTIONS,
        memoryItems: [],
        contextItems: [],
        createdAt: now,
        updatedAt: now,
      };
      const chat: Chat = {
        id: crypto.randomUUID(),
        projectId: project.id,
        title: 'New chat',
        createdAt: now,
        updatedAt: now,
      };
      if (shouldPersist) {
        await storage.putProject(project);
        await storage.putChat(chat);
      }
      const nextProjects = [project, ...projects.filter((p) => p.id !== project.id)];
      setProjects(nextProjects);
      setChats((prev) => [chat, ...prev]);
      setMessages([]);
      setActiveProjectId(project.id);
      setActiveChatId(chat.id);
      await persistSettings({
        ...settings,
        activeChatId: chat.id,
        activeProjectId: project.id,
        activeChatByProject: {
          ...settings.activeChatByProject,
          [project.id]: chat.id,
        },
      });
      return project;
    },
    [projects, settings, shouldPersist, persistSettings],
  );

  const updateProject = useCallback(
    async (id: string, patch: Partial<Project>) => {
      const existing = projects.find((p) => p.id === id) ?? (await storage.getProject(id));
      if (!existing) return;
      const updated: Project = {
        ...existing,
        ...patch,
        id: existing.id,
        updatedAt: Date.now(),
      };
      if (shouldPersist) await storage.putProject(updated);
      setProjects((prev) =>
        prev.map((p) => (p.id === id ? updated : p)).sort((a, b) => b.updatedAt - a.updatedAt),
      );
    },
    [projects, shouldPersist],
  );

  const createChat = useCallback(
    async (projectId?: string | null) => {
      // Explicit null → freestanding. Undefined → freestanding (New chat default).
      // Pass a project id string to attach to that project.
      const pid =
        projectId === undefined || projectId === null ? null : projectId;
      const now = Date.now();
      const chat: Chat = {
        id: crypto.randomUUID(),
        projectId: pid,
        title: 'New chat',
        createdAt: now,
        updatedAt: now,
      };
      if (shouldPersist) await storage.putChat(chat);

      setChats((prev) => [chat, ...prev.filter((c) => c.id !== chat.id)]);
      setActiveProjectId(pid ?? undefined);
      setActiveChatId(chat.id);
      setMessages([]);
      await persistSettings({
        ...settings,
        activeChatId: chat.id,
        activeProjectId: pid ?? undefined,
        activeChatByProject: pid
          ? {
              ...settings.activeChatByProject,
              [pid]: chat.id,
            }
          : settings.activeChatByProject,
      });
      if (pid) await updateProject(pid, {});
      return chat;
    },
    [shouldPersist, settings, persistSettings, updateProject],
  );

  const deleteProject = useCallback(
    async (id: string) => {
      if (shouldPersist) await storage.deleteProject(id);
      const remaining = projects.filter((p) => p.id !== id);
      setProjects(remaining);

      const nextActive = { ...settings.activeChatByProject };
      delete nextActive[id];

      if (activeProjectId === id) {
        if (remaining.length === 0) {
          // Drop into freestanding chat rather than forcing a General project
          await persistSettings({
            ...settings,
            activeChatByProject: nextActive,
            activeProjectId: undefined,
          });
          await createChat(null);
          return;
        }
        await persistSettings({
          ...settings,
          activeChatByProject: nextActive,
          activeProjectId: remaining[0].id,
        });
        await selectProject(remaining[0].id);
      } else {
        await persistSettings({ ...settings, activeChatByProject: nextActive });
      }
    },
    [shouldPersist, projects, settings, activeProjectId, createChat, persistSettings, selectProject],
  );

  const moveChatToProject = useCallback(
    async (chatId: string, projectId: string | null) => {
      const existing = chats.find((c) => c.id === chatId) ?? (await storage.getChat(chatId));
      if (!existing) return;
      const updated: Chat = {
        ...existing,
        projectId,
        updatedAt: Date.now(),
      };
      if (shouldPersist) await storage.putChat(updated);
      setChats((prev) =>
        prev
          .map((c) => (c.id === chatId ? updated : c))
          .sort((a, b) => b.updatedAt - a.updatedAt),
      );
      if (activeChatId === chatId) {
        setActiveProjectId(projectId ?? undefined);
        await persistSettings({
          ...settings,
          activeChatId: chatId,
          activeProjectId: projectId ?? undefined,
          activeChatByProject: projectId
            ? { ...settings.activeChatByProject, [projectId]: chatId }
            : settings.activeChatByProject,
        });
      }
      if (projectId) await updateProject(projectId, {});
    },
    [chats, shouldPersist, activeChatId, settings, persistSettings, updateProject],
  );

  const updateChat = useCallback(
    async (id: string, patch: Partial<Chat>) => {
      const existing = chats.find((c) => c.id === id) ?? (await storage.getChat(id));
      if (!existing) return;
      const updated: Chat = {
        ...existing,
        ...patch,
        id: existing.id,
        projectId: existing.projectId,
        updatedAt: Date.now(),
      };
      if (shouldPersist) await storage.putChat(updated);
      setChats((prev) =>
        prev.map((c) => (c.id === id ? updated : c)).sort((a, b) => b.updatedAt - a.updatedAt),
      );
    },
    [chats, shouldPersist],
  );

  const deleteChat = useCallback(
    async (id: string) => {
      const chat = chats.find((c) => c.id === id) ?? (await storage.getChat(id));
      if (!chat) return;
      if (shouldPersist) await storage.deleteChat(id);

      const remaining = chats.filter((c) => c.id !== id);
      setChats(remaining);

      if (activeChatId === id) {
        if (remaining.length === 0) {
          // Stay freestanding if deleted chat was freestanding
          await createChat(isFreestandingChat(chat) ? null : chat.projectId);
        } else {
          // Prefer next chat in same bucket (project or freestanding)
          const sameBucket = remaining.filter((c) =>
            isFreestandingChat(chat)
              ? isFreestandingChat(c)
              : c.projectId === chat.projectId,
          );
          await selectChat((sameBucket[0] ?? remaining[0]).id);
        }
      }
    },
    [chats, shouldPersist, activeChatId, createChat, selectChat],
  );

  const clearChatMessages = useCallback(
    async (chatId: string) => {
      if (shouldPersist) await storage.deleteMessagesForChat(chatId);
      if (chatId === activeChatId) setMessages([]);
      await updateChat(chatId, { title: 'New chat' });
    },
    [shouldPersist, activeChatId, updateChat],
  );

  const addMessage = useCallback(
    async (
      message: Omit<Message, 'id' | 'createdAt'> & { id?: string; createdAt?: number },
    ): Promise<Message> => {
      const full: Message = {
        id: message.id ?? crypto.randomUUID(),
        createdAt: message.createdAt ?? Date.now(),
        chatId: message.chatId,
        role: message.role,
        content: message.content,
        modelId: message.modelId,
        attachments: message.attachments,
        mentions: message.mentions,
        nostrContext: message.nostrContext,
      };

      if (shouldPersist) {
        await storage.putMessage(full);
        const chat = await storage.getChat(full.chatId);
        if (chat) {
          const patch: Partial<Chat> = { updatedAt: Date.now() };
          if (
            full.role === 'user' &&
            (chat.title === 'New chat' || !chat.title)
          ) {
            patch.title = titleFromMessage(full.content, 48, full.attachments);
          }
          await storage.putChat({ ...chat, ...patch, updatedAt: Date.now() });
          if (full.chatId === activeChatId) {
            setChats((prev) =>
              prev
                .map((c) =>
                  c.id === full.chatId
                    ? { ...c, ...patch, updatedAt: Date.now() }
                    : c,
                )
                .sort((a, b) => b.updatedAt - a.updatedAt),
            );
          }
          if (chat.projectId) await updateProject(chat.projectId, {});
        }
      } else if (full.role === 'user') {
        setChats((prev) =>
          prev.map((c) =>
            c.id === full.chatId && (c.title === 'New chat' || !c.title)
              ? {
                  ...c,
                  title: titleFromMessage(full.content, 48, full.attachments),
                  updatedAt: Date.now(),
                }
              : c,
          ),
        );
      }

      if (full.chatId === activeChatId) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === full.id)) {
            return prev.map((m) => (m.id === full.id ? full : m));
          }
          return [...prev, full];
        });
      }
      return full;
    },
    [shouldPersist, activeChatId, updateProject],
  );

  const updateMessage = useCallback(
    async (id: string, patch: Partial<Message>) => {
      // Prefer in-memory active messages; fall back is rare (flush after stream)
      setMessages((prev) => {
        const existing = prev.find((m) => m.id === id);
        if (!existing) return prev;
        const updated = { ...existing, ...patch, id: existing.id };
        if (shouldPersist) {
          void storage.putMessage(updated);
        }
        return prev.map((m) => (m.id === id ? updated : m));
      });
    },
    [shouldPersist],
  );

  const deleteMessage = useCallback(
    async (id: string) => {
      if (shouldPersist) await storage.deleteMessage(id);
      setMessages((prev) => prev.filter((m) => m.id !== id));
    },
    [shouldPersist],
  );

  const replaceMessages = useCallback(
    async (chatId: string, next: Message[]) => {
      if (shouldPersist) {
        await storage.deleteMessagesForChat(chatId);
        if (next.length) await storage.putMessages(next);
      }
      if (chatId === activeChatId) setMessages(next);
    },
    [shouldPersist, activeChatId],
  );

  const forkChat = useCallback(
    async (sourceChatId: string, throughMessageId?: string) => {
      const source =
        chats.find((c) => c.id === sourceChatId) ?? (await storage.getChat(sourceChatId));
      if (!source) throw new Error('Chat not found');

      const sourceMessages =
        sourceChatId === activeChatId
          ? messages
          : await storage.listMessages(sourceChatId);

      let slice = [...sourceMessages].sort((a, b) => a.createdAt - b.createdAt);
      if (throughMessageId) {
        const idx = slice.findIndex((m) => m.id === throughMessageId);
        if (idx >= 0) slice = slice.slice(0, idx + 1);
      }

      const now = Date.now();
      const newChat: Chat = {
        id: crypto.randomUUID(),
        projectId: source.projectId ?? null,
        title: source.title?.startsWith('Fork:')
          ? source.title
          : `Fork: ${source.title || 'chat'}`.slice(0, 80),
        instructionOverride: source.instructionOverride,
        modelOverride: source.modelOverride,
        createdAt: now,
        updatedAt: now,
      };

      const cloned: Message[] = slice.map((m, i) => ({
        ...m,
        id: crypto.randomUUID(),
        chatId: newChat.id,
        createdAt: now + i,
        attachments: m.attachments
          ? m.attachments.map((a) => ({ ...a, id: crypto.randomUUID() }))
          : undefined,
        mentions: m.mentions
          ? m.mentions.map((x) => ({ ...x, id: crypto.randomUUID() }))
          : undefined,
        nostrContext: m.nostrContext
          ? m.nostrContext.map((x) => ({ ...x, id: crypto.randomUUID() }))
          : undefined,
      }));

      if (shouldPersist) {
        await storage.putChat(newChat);
        if (cloned.length) await storage.putMessages(cloned);
      }

      setChats((prev) => [newChat, ...prev.filter((c) => c.id !== newChat.id)]);
      setActiveProjectId(newChat.projectId ?? undefined);
      setActiveChatId(newChat.id);
      setMessages(cloned);
      await persistSettings({
        ...settingsRef.current,
        activeChatId: newChat.id,
        activeProjectId: newChat.projectId ?? undefined,
        activeChatByProject: newChat.projectId
          ? {
              ...settingsRef.current.activeChatByProject,
              [newChat.projectId]: newChat.id,
            }
          : settingsRef.current.activeChatByProject,
      });
      return newChat;
    },
    [chats, activeChatId, messages, shouldPersist, persistSettings],
  );

  const searchWorkspace = useCallback(
    async (query: string, limit = 40): Promise<WorkspaceSearchHit[]> => {
      const q = query.trim().toLowerCase();
      if (!q) return [];

      // Multi-token: all tokens must appear (AND) for stronger full-text feel
      const tokens = q.split(/\s+/).filter((t) => t.length > 0);
      const matchesAll = (hay: string): number => {
        const h = hay.toLowerCase();
        if (!tokens.every((t) => h.includes(t))) return -1;
        // Prefer position of first token for snippet
        return h.indexOf(tokens[0]!);
      };

      const allChats = await storage.getAllChats();
      const allProjects = await storage.getAllProjects();
      const projectName = new Map(allProjects.map((p) => [p.id, p.name]));
      const hits: WorkspaceSearchHit[] = [];

      const snippetAround = (text: string, qi: number, len = 140): string => {
        const start = Math.max(0, qi - 48);
        const end = Math.min(text.length, qi + len);
        let s = text.slice(start, end).replace(/\s+/g, ' ').trim();
        if (start > 0) s = '…' + s;
        if (end < text.length) s = s + '…';
        return s;
      };

      // Project name, instructions, memory, context pack
      for (const project of allProjects) {
        if (hits.length >= limit) break;
        const nameIdx = matchesAll(project.name || '');
        if (nameIdx >= 0) {
          hits.push({
            chatTitle: project.name,
            projectId: project.id,
            projectName: project.name,
            snippet: `Project · ${project.name}`,
            updatedAt: project.updatedAt,
            matchKind: 'project',
          });
        }
        const instr = project.instructions || '';
        const instrIdx = matchesAll(instr);
        if (instrIdx >= 0) {
          hits.push({
            chatTitle: project.name,
            projectId: project.id,
            projectName: project.name,
            snippet: `Instructions · ${snippetAround(instr, instrIdx)}`,
            updatedAt: project.updatedAt,
            matchKind: 'project',
          });
        }
        for (const mem of project.memoryItems ?? []) {
          if (hits.length >= limit) break;
          const body = `${mem.title ?? ''} ${mem.content || ''}`;
          const idx = matchesAll(body);
          if (idx < 0) continue;
          hits.push({
            chatTitle: project.name,
            projectId: project.id,
            projectName: project.name,
            snippet: `Memory · ${snippetAround(body, idx)}`,
            updatedAt: mem.updatedAt || project.updatedAt,
            matchKind: 'memory',
          });
        }
        for (const item of project.contextItems ?? []) {
          if (hits.length >= limit) break;
          const body = `${item.title ?? ''} ${item.content || ''}`;
          const idx = matchesAll(body);
          if (idx < 0) continue;
          hits.push({
            chatTitle: project.name,
            projectId: project.id,
            projectName: project.name,
            snippet: `Context · ${snippetAround(body, idx)}`,
            updatedAt: item.createdAt || project.updatedAt,
            matchKind: 'context',
          });
        }
      }

      for (const chat of allChats) {
        if (hits.length >= limit) break;
        const title = chat.title || 'Untitled';
        const titleIdx = matchesAll(title);
        if (titleIdx >= 0) {
          hits.push({
            chatId: chat.id,
            chatTitle: title,
            projectId: chat.projectId,
            projectName: chat.projectId
              ? projectName.get(chat.projectId)
              : undefined,
            snippet: title,
            updatedAt: chat.updatedAt,
            matchKind: 'title',
          });
        }

        const msgs = await storage.listMessages(chat.id);
        for (const m of msgs) {
          if (hits.length >= limit) break;
          const body = m.content || '';
          const idx = matchesAll(body);
          if (idx < 0) {
            let foundAtt = false;
            for (const a of m.attachments ?? []) {
              const hay = `${a.name ?? ''} ${a.kind === 'text' ? a.data.slice(0, 4000) : ''}`;
              if (matchesAll(hay) >= 0) {
                hits.push({
                  chatId: chat.id,
                  chatTitle: title,
                  projectId: chat.projectId,
                  projectName: chat.projectId
                    ? projectName.get(chat.projectId)
                    : undefined,
                  messageId: m.id,
                  snippet: a.name || attachmentSummary(m.attachments) || 'Attachment',
                  role: m.role,
                  updatedAt: m.createdAt,
                  matchKind: 'attachment',
                });
                foundAtt = true;
                break;
              }
            }
            if (foundAtt) continue;
            continue;
          }
          hits.push({
            chatId: chat.id,
            chatTitle: title,
            projectId: chat.projectId,
            projectName: chat.projectId
              ? projectName.get(chat.projectId)
              : undefined,
            messageId: m.id,
            snippet: snippetAround(body, idx),
            role: m.role,
            updatedAt: m.createdAt,
            matchKind: 'message',
          });
        }
      }

      return hits.sort((a, b) => b.updatedAt - a.updatedAt).slice(0, limit);
    },
    [],
  );

  const addContextItem = useCallback(
    async (projectId: string, item: Omit<ContextItem, 'id' | 'createdAt'>) => {
      const project = projects.find((p) => p.id === projectId) ?? (await storage.getProject(projectId));
      if (!project) return;
      const full: ContextItem = {
        ...item,
        id: crypto.randomUUID(),
        createdAt: Date.now(),
      };
      await updateProject(projectId, {
        contextItems: [...project.contextItems, full],
      });
    },
    [projects, updateProject],
  );

  const updateContextItem = useCallback(
    async (projectId: string, itemId: string, patch: Partial<ContextItem>) => {
      const project = projects.find((p) => p.id === projectId) ?? (await storage.getProject(projectId));
      if (!project) return;
      await updateProject(projectId, {
        contextItems: project.contextItems.map((i) =>
          i.id === itemId ? { ...i, ...patch, id: i.id } : i,
        ),
      });
    },
    [projects, updateProject],
  );

  const removeContextItem = useCallback(
    async (projectId: string, itemId: string) => {
      const project = projects.find((p) => p.id === projectId) ?? (await storage.getProject(projectId));
      if (!project) return;
      await updateProject(projectId, {
        contextItems: project.contextItems.filter((i) => i.id !== itemId),
      });
    },
    [projects, updateProject],
  );

  const addMemoryItem = useCallback(
    async (
      projectId: string,
      item: Omit<MemoryItem, 'id' | 'createdAt' | 'updatedAt'>,
    ) => {
      const project =
        projects.find((p) => p.id === projectId) ?? (await storage.getProject(projectId));
      if (!project) return;
      const now = Date.now();
      const full: MemoryItem = {
        ...item,
        id: crypto.randomUUID(),
        createdAt: now,
        updatedAt: now,
        source: item.source ?? 'manual',
      };
      await updateProject(projectId, {
        memoryItems: [...(project.memoryItems ?? []), full],
      });
    },
    [projects, updateProject],
  );

  const updateMemoryItem = useCallback(
    async (projectId: string, itemId: string, patch: Partial<MemoryItem>) => {
      const project =
        projects.find((p) => p.id === projectId) ?? (await storage.getProject(projectId));
      if (!project) return;
      await updateProject(projectId, {
        memoryItems: (project.memoryItems ?? []).map((i) =>
          i.id === itemId
            ? { ...i, ...patch, id: i.id, updatedAt: Date.now() }
            : i,
        ),
      });
    },
    [projects, updateProject],
  );

  const removeMemoryItem = useCallback(
    async (projectId: string, itemId: string) => {
      const project =
        projects.find((p) => p.id === projectId) ?? (await storage.getProject(projectId));
      if (!project) return;
      await updateProject(projectId, {
        memoryItems: (project.memoryItems ?? []).filter((i) => i.id !== itemId),
      });
    },
    [projects, updateProject],
  );

  const exportAll = useCallback(async () => {
    await exportWorkspaceToFile();
  }, []);

  const exportProject = useCallback(async (projectId: string) => {
    await exportWorkspaceToFile({ projectIds: [projectId] });
  }, []);

  const exportEncrypted = useCallback(async (passphrase: string) => {
    await exportEncryptedWorkspaceToFile(passphrase);
  }, []);

  const importFile = useCallback(
    async (file: File, mode: 'merge' | 'replace' = 'merge', passphrase?: string) => {
      const raw = await readImportFile(file);
      await importWorkspaceFromJson(raw, mode, passphrase);
      await refresh();
    },
    [refresh],
  );

  const activeProject = useMemo(
    () => projects.find((p) => p.id === activeProjectId) ?? null,
    [projects, activeProjectId],
  );

  const activeChat = useMemo(
    () => chats.find((c) => c.id === activeChatId) ?? null,
    [chats, activeChatId],
  );

  const recentChats = useMemo(
    () =>
      chats
        .filter((c) => isFreestandingChat(c))
        .sort((a, b) => b.updatedAt - a.updatedAt),
    [chats],
  );

  const value: WorkspaceContextValue = {
    ready,
    settings,
    projects,
    chats,
    recentChats,
    messages,
    activeProject,
    activeChat,
    setHistoryMode,
    setDefaultModelId,
    setGlobalInstructions,
    updateSettings,
    selectProject,
    selectChat,
    createProject,
    updateProject,
    deleteProject,
    createChat,
    moveChatToProject,
    updateChat,
    deleteChat,
    clearChatMessages,
    addMessage,
    updateMessage,
    deleteMessage,
    replaceMessages,
    forkChat,
    searchWorkspace,
    addContextItem,
    updateContextItem,
    removeContextItem,
    addMemoryItem,
    updateMemoryItem,
    removeMemoryItem,
    exportAll,
    exportProject,
    exportEncrypted,
    importFile,
    refresh,
    shouldPersist,
  };

  return (
    <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>
  );
}

export function useWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error('useWorkspace must be used within WorkspaceProvider');
  }
  return ctx;
}
